import {Link} from 'react-router-dom';
import {usePlaces} from '../api/places';
import EmptyState from '../components/feedback/EmptyState';
import ErrorBox from '../components/feedback/ErrorBox';
import {SkeletonList} from '../components/feedback/Skeleton';
import Icon from '../components/Icon';
import {groupPlaces, PLACE_TYPE_ICONS, PLACE_TYPE_LABELS} from '../lib/placeTree';
import {Place} from '../types';

// The Garden tab (wireframe "garden"): the places as a front door, not a setting. Each area is
// a photo card with its beds beneath; every one opens its place page. The photo is the place's
// own, else the newest tell photo there. Tidying (order, hide, remove) stays in More → Places.
// Phase 4 adds the aerial map here as a List / Map switch.

const tellsLabel = (n: number) => (n === 0 ? 'no tells yet' : n === 1 ? '1 tell' : `${n} tells`);
const bedsLabel = (n: number) => (n === 1 ? '1 bed' : `${n} beds`);

function AreaCard({area, beds}: {area: Place; beds: number}) {
    return (
        <Link to={`/places/${area.id}`} className="card block overflow-hidden no-underline">
            {area.coverUrl ? (
                <div className="h-[150px] bg-sunken">
                    <img src={area.coverUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
                </div>
            ) : null}
            <div className="flex items-center gap-3 px-4 py-[14px]">
                {!area.coverUrl && (
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[11px] bg-kind-plain-bg text-kind-plain-fg">
                        <Icon name="garden" size={20} />
                    </span>
                )}
                <span className="min-w-0 flex-1">
                    <span className="block truncate font-display text-lg font-[650] leading-[1.2]">{area.name}</span>
                    <span className="meta block truncate">{[beds > 0 ? bedsLabel(beds) : null, tellsLabel(area.tellCount), area.soil].filter(Boolean).join(' · ')}</span>
                </span>
                <Icon name="chevron" size={20} className="shrink-0 text-ink-3" />
            </div>
        </Link>
    );
}

function BedRow({bed}: {bed: Place}) {
    return (
        <Link to={`/places/${bed.id}`} className="flex min-h-[60px] items-center gap-3 px-4 py-2 no-underline">
            {bed.coverThumbUrl ? (
                <img src={bed.coverThumbUrl} alt="" loading="lazy" className="h-11 w-11 shrink-0 rounded-[12px] object-cover" />
            ) : (
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] bg-kind-grow-bg text-kind-grow-fg">
                    <Icon name={PLACE_TYPE_ICONS[bed.type]} size={20} />
                </span>
            )}
            <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold">{bed.name}</span>
                <span className="meta block truncate">{[PLACE_TYPE_LABELS[bed.type], tellsLabel(bed.tellCount), bed.size].filter(Boolean).join(' · ')}</span>
            </span>
            <Icon name="chevron" size={20} className="shrink-0 text-ink-3" />
        </Link>
    );
}

export default function GardenPage() {
    const places = usePlaces(false);
    const groups = groupPlaces(places.data ?? []);
    const beds = groups.reduce((n, g) => n + g.spots.length, 0);

    return (
        <div className="settle-in flex flex-col gap-5">
            <header className="flex items-end justify-between pt-2">
                <div>
                    <h1 className="display m-0">Garden</h1>
                    {places.isSuccess && groups.length > 0 && (
                        <p className="meta mt-1">
                            {groups.length} {groups.length === 1 ? 'area' : 'areas'}
                            {beds > 0 ? ` · ${bedsLabel(beds)}` : ''}
                        </p>
                    )}
                </div>
                <Link to="/places" className="btn btn-secondary btn-sm no-underline">
                    <Icon name="edit" size={18} /> Tidy
                </Link>
            </header>

            {places.isPending && <SkeletonList rows={3} kind="card" />}
            {places.isError && <ErrorBox error={places.error} onRetry={() => void places.refetch()} />}

            {places.isSuccess && groups.length === 0 && (
                <EmptyState icon="garden" title="No places yet." action={<Link to="/places" className="btn btn-primary no-underline">Add your first area</Link>}>
                    Areas like the back garden or the tunnel house, with beds and pots inside them.
                </EmptyState>
            )}

            {groups.map(({area, spots}) => (
                <section key={area.id} className="flex flex-col gap-2">
                    <AreaCard area={area} beds={spots.length} />
                    {spots.length > 0 && (
                        <div className="rows ml-4 rounded-[18px] border-[1.5px] border-line bg-surface">
                            {spots.map(bed => (
                                <BedRow key={bed.id} bed={bed} />
                            ))}
                        </div>
                    )}
                </section>
            ))}

            {places.isSuccess && groups.length > 0 && (
                <Link to="/places" className="meta self-start no-underline underline-offset-2 hover:underline">
                    Add an area or a bed, rename, reorder or hide them in Places
                </Link>
            )}
        </div>
    );
}
